import { useCallback } from 'react';
import { classifyReadingSeverity } from './videoRowSeverity';

export const THRESHOLD_ACK_EVENT = 'shm-threshold-ack';
const ACK_STORAGE_KEY = 'shm-sensor-threshold-ack-v1';

const AXIS_KEYS = ['xAxis', 'yAxis', 'zAxis'];
const BAND_KEYS = ['normal', 'elevated', 'significant'];
const ALERT_BANDS = new Set(['elevated', 'significant', 'critical']);

const normalizeId = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'object' && value._id != null) return String(value._id);
  return String(value);
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const finiteNum = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const bandIsSet = (band) => {
  if (!band || typeof band !== 'object') return false;
  return finiteNum(band.lower) != null && finiteNum(band.upper) != null;
};

/** True when Normal, Elevated, and Significant bounds exist on at least one axis. */
export const sensorHasThresholdPoints = (sensor) => {
  const spec = sensor?.specLimits;
  if (!spec || typeof spec !== 'object') return false;
  return AXIS_KEYS.some((axis) => {
    const limits = spec[axis];
    if (!limits || typeof limits !== 'object') return false;
    return BAND_KEYS.every((band) => bandIsSet(limits[band]));
  });
};

/** Same shapes the live chart accepts: nested bands, or flat normalU / elevU / sigU. */
const normalizeAxisLimits = (lim) => {
  if (!lim || typeof lim !== 'object') return {};
  if (lim.normal || lim.elevated || lim.significant) {
    return {
      normal: lim.normal,
      elevated: lim.elevated,
      significant: lim.significant,
    };
  }
  if (
    lim.normalU != null || lim.normalL != null
    || lim.elevU != null || lim.elevL != null
    || lim.sigU != null || lim.sigL != null
  ) {
    return {
      normal: { upper: lim.normalU, lower: lim.normalL },
      elevated: { upper: lim.elevU, lower: lim.elevL },
      significant: { upper: lim.sigU, lower: lim.sigL },
    };
  }
  return {};
};

/**
 * Live-chart line positions. A saved data point N is drawn at +N and -N,
 * not at the raw signed lower/upper pair.
 */
const chartBandSpan = (band) => {
  const upper = finiteNum(band?.upper);
  const lower = finiteNum(band?.lower);
  if (upper == null && lower == null) return null;
  const hi = upper != null ? Math.abs(upper) : Math.abs(lower);
  const lo = lower != null ? -Math.abs(lower) : -Math.abs(upper);
  return { lo: Math.min(lo, hi), hi: Math.max(lo, hi) };
};

const sharedNormalBand = (spec) => {
  for (let i = 0; i < AXIS_KEYS.length; i += 1) {
    const normal = normalizeAxisLimits(spec?.[AXIS_KEYS[i]]).normal;
    if (chartBandSpan(normal)) return normal;
  }
  return null;
};

const limitsForChartAxis = (spec, axis) => {
  const limits = normalizeAxisLimits(spec?.[axis]);
  if (chartBandSpan(limits.normal)) return limits;
  const shared = sharedNormalBand(spec);
  if (!shared) return limits;
  return { ...limits, normal: shared };
};

const RAW_AXIS_KEYS = {
  xAxis: ['VIBRATION_X(mg)'],
  yAxis: ['VIBRATION_Y(mg)'],
  zAxis: ['VIBRATION_Z(mg)'],
};

const axisValue = (reading, axis) => {
  const direct = finiteNum(reading?.[axis]);
  if (direct != null) return direct;
  const vib = reading?.vibration;
  const fromVib = finiteNum(vib?.[axis]);
  if (fromVib != null) return fromVib;
  const raw = reading?.rawPayload;
  const fromRaw = finiteNum(raw?.[axis]);
  if (fromRaw != null) return fromRaw;
  const rawKeys = RAW_AXIS_KEYS[axis] || [];
  for (let i = 0; i < rawKeys.length; i += 1) {
    const fromNamed = finiteNum(raw?.[rawKeys[i]]);
    if (fromNamed != null) return fromNamed;
  }
  const samples = reading?.[`${axis}Samples`] || reading?._axisSamples?.[axis];
  if (!Array.isArray(samples)) return null;
  for (let i = samples.length - 1; i >= 0; i -= 1) {
    const sample = samples[i];
    const n = finiteNum(sample && typeof sample === 'object' ? sample.value : sample);
    if (n != null) return n;
  }
  return null;
};

const BAND_RANK = { elevated: 1, significant: 2, critical: 3 };
const AXIS_HOVER_LABEL = { xAxis: 'X-Axis', yAxis: 'Y-Axis', zAxis: 'Z-Axis' };
const BAND_HOVER_LABEL = {
  normal: 'Normal',
  elevated: 'Elevated',
  significant: 'Significant',
  critical: 'Critical',
};

const joinAxisLabels = (axes) => {
  const labels = AXIS_KEYS
    .filter((axis) => axes.includes(axis))
    .map((axis) => AXIS_HOVER_LABEL[axis]);
  if (labels.length <= 1) return labels[0] || '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
};

/** Hover text. Normal stays as-is. An alert names only the axes at that worst band. */
const bandHoverTitle = (band, axes) => {
  const name = BAND_HOVER_LABEL[band] || 'Threshold';
  const base = `Last data in ${name} range`;
  if (!axes || !axes.length || band === 'normal') return base;
  return `${base}: ${joinAxisLabels(axes)}`;
};

/**
 * Worst band for this reading, plus the axes that sit in that band.
 * null when the reading cannot be placed on the live-chart threshold lines.
 */
const classifyAxesOnChartBands = (sensor, reading) => {
  const spec = sensor?.specLimits;
  if (!spec || typeof spec !== 'object' || !reading) return null;
  let sawValue = false;
  let worst = null;
  const axisBands = [];
  AXIS_KEYS.forEach((axis) => {
    const value = axisValue(reading, axis);
    if (value == null) return;
    const limits = limitsForChartAxis(spec, axis);
    const significant = chartBandSpan(limits.significant);
    const elevated = chartBandSpan(limits.elevated);
    const normal = chartBandSpan(limits.normal);
    if (!significant && !elevated && !normal) return;
    sawValue = true;
    let sev = null;
    if (significant && (value < significant.lo || value > significant.hi)) sev = 'critical';
    else if (elevated && (value < elevated.lo || value > elevated.hi)) sev = 'significant';
    else if (normal && (value < normal.lo || value > normal.hi)) sev = 'elevated';
    if (sev) axisBands.push({ axis, sev });
    if (sev && (!worst || BAND_RANK[sev] > BAND_RANK[worst])) worst = sev;
  });
  if (!sawValue) return null;
  const band = worst || 'normal';
  return {
    band,
    axes: axisBands.filter((item) => item.sev === band).map((item) => item.axis),
  };
};

/** null when this reading cannot be placed on the live-chart threshold lines. */
const classifyReadingOnChartBands = (sensor, reading) => {
  const detail = classifyAxesOnChartBands(sensor, reading);
  return detail ? detail.band : null;
};

const readingTimestampMs = (reading) => {
  const t = new Date(reading?.timestamp || 0).getTime();
  return Number.isFinite(t) && t > 0 ? t : 0;
};

const readAckMap = () => {
  try {
    const raw = window.localStorage.getItem(ACK_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeAckMap = (map) => {
  try {
    window.localStorage.setItem(ACK_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota / private mode */
  }
};

export const getThresholdAck = (sensorId) => {
  const id = normalizeId(sensorId);
  if (!id) return null;
  const entry = readAckMap()[id];
  if (!entry || typeof entry !== 'object') return null;
  const readingTs = Number(entry.readingTs);
  if (!Number.isFinite(readingTs) || readingTs <= 0) return null;
  return { readingTs, level: String(entry.level || '') };
};

export const acknowledgeSensorThresholdAlert = (sensor, reading) => {
  const sensorId = normalizeId(sensor?._id || sensor?.id);
  if (!sensorId || typeof window === 'undefined') return false;
  const readingTs = readingTimestampMs(reading) || Date.now();
  const level = classifyLastDataBand(sensor, reading);
  const next = readAckMap();
  next[sensorId] = { readingTs, level, at: Date.now() };
  writeAckMap(next);
  window.dispatchEvent(new CustomEvent(THRESHOLD_ACK_EVENT, { detail: { sensorId, readingTs, level } }));
  return true;
};

const isAcknowledged = (sensorId, reading, band) => {
  if (!ALERT_BANDS.has(band)) return false;
  const ack = getThresholdAck(sensorId);
  if (!ack) return false;
  const readingTs = readingTimestampMs(reading);
  if (!readingTs) return ack.readingTs > 0;
  return ack.readingTs >= readingTs;
};

/** Last-data band: normal | elevated | significant | critical | null (no reading). */
export const classifyLastDataBand = (sensor, reading) => {
  if (!reading) return null;
  const onChart = classifyReadingOnChartBands(sensor, reading);
  if (onChart) return onChart;
  const fromReading = String(reading.severity || '').trim().toLowerCase();
  if (fromReading === 'normal') return 'normal';
  const classified = classifyReadingSeverity(reading, sensor ? [sensor] : []);
  if (classified === 'critical' || classified === 'significant' || classified === 'elevated') {
    return classified;
  }
  if (fromReading === 'critical' || fromReading === 'significant' || fromReading === 'elevated') {
    return fromReading;
  }
  return 'normal';
};

const THRESHOLD_UNAVAILABLE_TITLE = 'Threshold status unavailable';

/**
 * Display variant for the extra threshold icon beside wifi.
 * disabled — no last reading, or no way to place that reading on a threshold line.
 * A sensor-health blink does not hide a last point that is in Normal.
 * normal (green) — last data sits inside the live-chart Normal lines,
 *   or the user acknowledged yellow/orange/red
 * elevated (yellow) / significant (orange) / critical (red)
 */
export const resolveThresholdStatus = ({
  sensor,
  reading,
  wifiBlinking = false,
} = {}) => {
  const hasPoints = sensorHasThresholdPoints(sensor);
  const chartDetail = reading ? classifyAxesOnChartBands(sensor, reading) : null;
  const onChart = chartDetail ? chartDetail.band : null;
  const band = reading ? (onChart || (hasPoints ? (classifyLastDataBand(sensor, reading) || 'normal') : null)) : null;
  if (wifiBlinking && band !== 'normal') {
    return {
      variant: 'disabled',
      blink: false,
      title: THRESHOLD_UNAVAILABLE_TITLE,
    };
  }
  if (onChart === 'normal') {
    return {
      variant: 'normal',
      blink: false,
      title: 'Last data in Normal range',
    };
  }
  if (!hasPoints || !reading || !band) {
    return {
      variant: 'disabled',
      blink: false,
      title: THRESHOLD_UNAVAILABLE_TITLE,
    };
  }

  const sensorId = normalizeId(sensor?._id || sensor?.id);
  const acked = isAcknowledged(sensorId, reading, band);
  const variant = acked ? 'normal' : band;
  const blink = ALERT_BANDS.has(variant);
  const axes = chartDetail && chartDetail.band === variant ? chartDetail.axes : [];
  const title = acked
    ? 'Threshold alert acknowledged'
    : bandHoverTitle(variant, axes);
  return {
    variant,
    blink,
    title: title || 'Threshold status',
  };
};

const VARIANT_RANK = {
  disabled: 0,
  normal: 1,
  elevated: 2,
  significant: 3,
  critical: 4,
};

/** Logger icon: worst child sensor colour. critical > significant > elevated > normal. */
export const resolveLoggerThresholdStatus = (sensors = []) => {
  const statuses = (sensors || []).map((sensor) => resolveThresholdStatus({
    sensor,
    reading: sensor.reading,
    wifiBlinking: sensor.wifiLevel === 'error',
  }));
  if (!statuses.length) {
    return {
      variant: 'disabled',
      blink: false,
      title: 'Threshold status unavailable',
    };
  }
  let worst = statuses[0];
  statuses.forEach((status) => {
    if ((VARIANT_RANK[status.variant] || 0) > (VARIANT_RANK[worst.variant] || 0)) {
      worst = status;
    }
  });
  const titles = {
    critical: 'Highest sensor alert: Critical',
    significant: 'Highest sensor alert: Significant',
    elevated: 'Highest sensor alert: Elevated',
    normal: 'All sensors in Normal range',
    disabled: 'Threshold status unavailable',
  };
  return {
    variant: worst.variant,
    blink: ALERT_BANDS.has(worst.variant),
    title: titles[worst.variant] || worst.title,
  };
};

const thresholdIconHtml = ({ variant, title }) => (
  `<span class="sensor-threshold-status-icon sensor-threshold-status-icon--${escapeHtml(variant)}" title="${escapeHtml(title)}">`
  + '<i class="ti ti-wifi"></i>'
  + '</span>'
);

export const buildThresholdStatusIconHtml = (options) =>
  thresholdIconHtml(resolveThresholdStatus(options));

export const buildLoggerThresholdStatusIconHtml = (sensors) =>
  thresholdIconHtml(resolveLoggerThresholdStatus(sensors));

/** Click the live chart to acknowledge yellow / orange / red for the selected sensor(s). */
export const useAcknowledgeThresholdOnChart = ({ sensors = [], latestByKey = {} } = {}) =>
  useCallback(() => {
    const list = Array.isArray(sensors) ? sensors.filter(Boolean) : [];
    list.forEach((sensor) => {
      const id = normalizeId(sensor._id);
      const serial = String(sensor.sensorSerialNo || '').trim();
      const reading = (id && latestByKey[id])
        || (serial && latestByKey[serial])
        || null;
      acknowledgeSensorThresholdAlert(sensor, reading);
    });
  }, [sensors, latestByKey]);
